import { useCallback, useEffect, useRef, useState } from "react";
import type { ArgusGatewayEvent } from "../../types/argus-events";

/**
 * Give up after this many consecutive reconnect attempts. The manual
 * `reconnect()` callback resets the counter, so the user can always
 * click "Reconnect" to try again.
 */
const MAX_RECONNECT_ATTEMPTS = 3;

export interface UseArgusSSEOptions {
  /** Called for each new event */
  onEvent?: (event: ArgusGatewayEvent) => void;
  /** Called on auth failure (401/403) — trigger instance detail re-fetch */
  onAuthError?: () => void;
  /** Enable auto-reconnect (default: true) */
  autoReconnect?: boolean;
  /** Pause when page hidden (default: true) */
  pauseWhenHidden?: boolean;
}

export interface UseArgusSSEReturn {
  /** Whether SSE connection is active */
  isConnected: boolean;
  /** Connection error message, if any */
  error: string | null;
  /** Manually trigger reconnection */
  reconnect: () => void;
  /** Disconnect SSE */
  disconnect: () => void;
}

/**
 * SSE hook for the Argus gateway event stream.
 *
 * Purpose-built for the gateway SSE format (raw `data: {type, ...}` lines,
 * no `id:` or `event:` SSE fields). Uses `fetch()` for custom header support.
 *
 * Does NOT accumulate events — dispatches each event via `onEvent` callback.
 * Does NOT load transcript on connect — consumer's responsibility.
 *
 * @param hostUrl - Gateway base URL (e.g. `https://<sandbox>.e2b.dev`)
 * @param sessionId - Active session ID
 * @param accessToken - e2b traffic access token
 * @param options - SSE options
 */
export const useArgusSSE = (
  hostUrl: string | null,
  sessionId: string | null,
  accessToken: string | null,
  options?: UseArgusSSEOptions,
): UseArgusSSEReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [_reconnectTrigger, setReconnectTrigger] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(
    typeof document !== "undefined" ? !document.hidden : true,
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  const onEventRef = useRef(options?.onEvent);
  const onAuthErrorRef = useRef(options?.onAuthError);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectedManuallyRef = useRef(false);

  const autoReconnect = options?.autoReconnect !== false;
  const pauseWhenHidden = options?.pauseWhenHidden !== false;

  // Keep callback refs updated
  useEffect(() => {
    onEventRef.current = options?.onEvent;
  }, [options?.onEvent]);

  useEffect(() => {
    onAuthErrorRef.current = options?.onAuthError;
  }, [options?.onAuthError]);

  // Page Visibility API handler
  useEffect(() => {
    if (typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Main SSE connection effect
  // biome-ignore lint/correctness/useExhaustiveDependencies: _reconnectTrigger is intentionally used to force reconnection
  useEffect(() => {
    // Don't connect if essential params are missing. The `accessToken` is
    // *optional* — e2b templates that aren't built with traffic access
    // tokens legitimately return `null` from `sandbox.traffic_access_token`
    // and don't enforce the `e2b-traffic-access-token` header. We only
    // attach the header when the token is present.
    if (!hostUrl || !sessionId) {
      return;
    }

    // Don't connect if manually disconnected
    if (disconnectedManuallyRef.current) {
      return;
    }

    // Don't connect if page is hidden and pauseWhenHidden is enabled
    if (pauseWhenHidden && !isPageVisible) {
      return;
    }

    // Clean up previous connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const getBackoffDelay = (attempts: number) => {
      const baseDelay = 1000;
      const maxDelay = 30000;
      return Math.min(baseDelay * 2 ** attempts, maxDelay);
    };

    const scheduleReconnect = () => {
      if (!autoReconnect || disconnectedManuallyRef.current) return;
      if (pauseWhenHidden && !isPageVisible) return;
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        setError(
          `Disconnected after ${MAX_RECONNECT_ATTEMPTS} failed reconnect attempts — click to retry`,
        );
        return;
      }

      const delay = getBackoffDelay(reconnectAttemptsRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectAttemptsRef.current += 1;
        setReconnectTrigger((prev) => prev + 1);
      }, delay);
    };

    const connect = async () => {
      try {
        const url = `${hostUrl.replace(/\/+$/, "")}/session/manage/stream/${sessionId}`;

        const headers: Record<string, string> = {
          Accept: "text/event-stream",
        };
        if (accessToken) {
          headers["e2b-traffic-access-token"] = accessToken;
        }

        const response = await fetch(url, {
          headers,
          signal: abortController.signal,
        });

        if (!response.ok) {
          // Auth errors — call onAuthError instead of reconnecting
          if (response.status === 401 || response.status === 403) {
            setError(`Authentication failed (${response.status})`);
            setIsConnected(false);
            onAuthErrorRef.current?.();
            return;
          }

          const body = await response.json().catch(() => null);
          const message = body?.error ?? `HTTP error ${response.status}`;
          throw new Error(message);
        }

        if (!response.body) {
          throw new Error("Response body is null");
        }

        // Successful connection — reset backoff
        reconnectAttemptsRef.current = 0;
        setIsConnected(true);
        setError(null);

        // Read the stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            setIsConnected(false);
            scheduleReconnect();
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // Process complete lines
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            // Empty line = end of SSE frame (we only parse `data:` lines)
            if (line.trim() === "") continue;

            // Parse `data:` prefix
            if (line.startsWith("data:")) {
              const jsonStr = line.slice(5).trim();
              if (!jsonStr) continue;

              try {
                const event = JSON.parse(jsonStr) as ArgusGatewayEvent;
                onEventRef.current?.(event);
              } catch {
                // Skip malformed JSON
              }
            }
            // Ignore other SSE fields (id:, event:, retry:)
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          setIsConnected(false);
          return;
        }

        setIsConnected(false);
        setError(err instanceof Error ? err.message : "Unknown connection error");
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      abortController.abort();
      abortControllerRef.current = null;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      setIsConnected(false);
    };
  }, [
    hostUrl,
    sessionId,
    accessToken,
    isPageVisible,
    autoReconnect,
    pauseWhenHidden,
    _reconnectTrigger,
  ]);

  const reconnect = useCallback(() => {
    disconnectedManuallyRef.current = false;
    reconnectAttemptsRef.current = 0;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setError(null);
    setReconnectTrigger((prev) => prev + 1);
  }, []);

  const disconnect = useCallback(() => {
    disconnectedManuallyRef.current = true;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setIsConnected(false);
    setError(null);
  }, []);

  return {
    isConnected,
    error,
    reconnect,
    disconnect,
  };
};
